// FIȘA SELECȚIEI de pe planul de situație, pentru agentul planului: ce a
// apăsat omul pe planșă când a cerut ceva («mută aleea asta», «mai pune două
// lângă ăsta»), ca dict mic, fără geometrie — ajunge pe job ca `tinta` și în
// promptul de editare al agentului (`agent_plan/prompt.mesaj_editare`), la fel
// cum fațada își trimite fișele (`viewer/fatada/selectie.ts`).
//
// Agentul își recunoaște obiectele după ID-UL LUI: locul după numărul de pe
// plăcuță (`nr`, același cu `data-nr` din SVG-ul lui), aleea, trotuarul,
// trecerea și zona după id-ul din `props["site_plan.agent"]` («N3», «T12»,
// «X2», «Z1»). Corpurile și scările de evacuare sunt fixe pe planșa lui, cu
// codul lor («C2», «SE1»). `unde` e centrul, în metri, în cadrul documentului
// (același ca al SVG-ului agentului).
import type { SiteLayer } from "../client";
import type { Selection } from "../inspect";

export type FisaSituatie = {
  /** felul obiectului, cum îl numește agentul (`fel` din SVG-ul lui) */
  fel: "loc" | "alee" | "trotuar" | "trecere" | "zona" | "corp" | "scara_evacuare" | "acces";
  /** id-ul agentului; lipsă pe un obiect al motorului (o alee a pasului 2) */
  id?: string;
  /** numărul locului de pe plăcuță (`display.n`); omul spune «locul 57» */
  nr?: number;
  /** loc roșu: desenat, dar neconform */
  valid?: boolean;
  /** eticheta motorului («T3») pe o alee a pasului 2, fără id de agent */
  eticheta?: string;
  /** aleea traversată de o trecere de pietoni */
  alee?: string;
  /** felul zonei exterioare (`playground`, `waste_platform`, `square`) */
  zona?: string;
  unde?: [number, number];
};

const rotunjit = (p: number[]): [number, number] =>
  [Math.round(p[0] * 100) / 100, Math.round(p[1] * 100) / 100];

const centrul = (poly: number[][] | undefined): [number, number] | undefined => {
  if (!poly || poly.length < 1) return undefined;
  const n = poly.length;
  return rotunjit([poly.reduce((a, p) => a + p[0], 0) / n, poly.reduce((a, p) => a + p[1], 0) / n]);
};

/** Fișa obiectului ales de pe stratul sitului; `null` când nu e ales nimic de
 *  pe sit, sau ceva fără înțeles pentru agent (un etaj, o cameră de subsol). */
export function fisaSituatiei(sel: Selection | null | undefined, site: SiteLayer | null | undefined): FisaSituatie | null {
  if (!sel || !site || sel.level !== "site") return null;
  switch (sel.kind) {
    case "stall": {
      const s = site.stalls?.[sel.i];
      if (!s) return null;
      return { fel: "loc", ...(s.agent ? { id: s.agent } : {}), ...(typeof s.n === "number" ? { nr: s.n } : {}),
               ...(s.valid === false ? { valid: false } : {}), unde: rotunjit([s.cx, s.cy]) };
    }
    case "siteRoad": {
      const r = site.roads?.[sel.i];
      if (!r || Array.isArray(r)) return null;
      const ax = r.path ?? (r.p1 && r.p2 ? [r.p1, r.p2] : undefined);
      return { fel: "alee", ...(r.agent ? { id: r.agent } : {}), ...(r.eticheta ? { eticheta: r.eticheta } : {}),
               unde: centrul(ax) ?? centrul(r.poly) };
    }
    case "siteSidewalk": {
      const sh = site.sidewalks?.[sel.i];
      if (!sh) return null;
      const o = Array.isArray(sh) ? { poly: sh } : sh;
      return { fel: "trotuar", ...(o.agent ? { id: o.agent } : {}), unde: centrul(o.axis ?? o.poly) };
    }
    case "siteCrossing": {
      const c = site.crossings?.[sel.i];
      if (!c) return null;
      return { fel: "trecere", ...(c.agent ? { id: c.agent } : {}), ...(c.alee ? { alee: c.alee } : {}),
               unde: centrul(c.axis ?? c.poly) };
    }
    case "siteZone": {
      const z = site.zones?.[sel.i];
      if (!z) return null;
      return { fel: "zona", ...(z.agent ? { id: z.agent } : {}), zona: z.kind, unde: centrul(z.poly) };
    }
    case "siteBar": {
      const b = site.bars?.[sel.i];
      if (!b) return null;
      return { fel: "corp", ...(b.code ? { id: b.code } : {}), unde: centrul(b.pts) };
    }
    case "siteStair": {
      const s = site.scariEvacuare?.[sel.i];
      if (!s) return null;
      return { fel: "scara_evacuare", id: s.id, unde: centrul(s.contur) };
    }
    case "siteEntrance": {
      const e = site.entrances?.[sel.i];
      if (!e) return null;
      return { fel: "acces", unde: rotunjit([e.x, e.y]) };
    }
    default:
      return null;
  }
}

/** Numele scurt al fișei, pe cipul din discuție: id-ul agentului, sau numărul
 *  locului, sau eticheta motorului. */
export function numeleFisei(f: FisaSituatie): string {
  return f.id ?? (f.nr != null ? `#${f.nr}` : f.eticheta ?? "");
}
